import { Flame } from "lucide-react";
import { MAX_STARS } from "@versedrill/core";
import { localDayKey } from "@versedrill/core";
import { effectiveStreak } from "@/lib/activity.ts";
import { starDistribution, useStore } from "@/lib/store.ts";

/** Current/best streak plus the histogram of stars earned. Shared by the result screen and the progress page. */
export function StatsBlock({ showStreaks = true }: { showStreaks?: boolean }) {
  const streaks = useStore((s) => s.streaks);
  const stars = useStore((s) => s.stars);
  const dist = starDistribution(stars);
  const most = Math.max(1, ...Object.values(dist));

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-7">
      {showStreaks && <div className="grid w-full grid-cols-2 gap-3 font-ui">
        {[
          { label: "Current streak", value: effectiveStreak(streaks, localDayKey(new Date())) },
          { label: "Best streak", value: streaks.longestStreak },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card px-4 py-3 text-center">
            <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
              <Flame className="size-3.5 text-brand" aria-hidden /> {s.label}
            </div>
            <div className="font-ui text-4xl font-semibold leading-tight tabular-nums">
              {s.value}
              <span className="ml-1 font-ui text-sm font-normal text-muted-foreground">{s.value === 1 ? "day" : "days"}</span>
            </div>
          </div>
        ))}
      </div>}

      <figure className="w-full font-ui" aria-label="Your star history">
        <figcaption className="mb-2 text-center text-xs uppercase tracking-[0.18em] text-muted-foreground">Your results</figcaption>
        {Object.keys(dist).length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">Solve a verse and your stars will appear here.</p>
        ) : (
          <div className="grid grid-cols-[3.2rem_1fr] items-center gap-x-3 gap-y-1.5 text-sm">
            {Array.from({ length: MAX_STARS }, (_, i) => MAX_STARS - i).map((n) => (
              <div key={n} className="contents">
                <span className="text-right tabular-nums text-muted-foreground">{n} ★</span>
                <div className="h-5 rounded-sm bg-secondary">
                  <div
                    className="flex h-full min-w-6 items-center justify-end rounded-sm bg-hint px-1.5 text-xs font-semibold text-hint-foreground"
                    style={{ width: `${Math.max(dist[n] ? 8 : 0, ((dist[n] ?? 0) / most) * 100)}%`, opacity: dist[n] ? 1 : 0 }}
                  >
                    {dist[n]}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </figure>
    </div>
  );
}
